import { useState } from "react";
import PageHeader from "../components/layout/PageHeader";
import { EmptyState, ErrorState, KpiSkeletonRow } from "../components/ui/states";
import { useApi } from "../lib/useApi";
import { api, qs, ApiClientError } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/ui/Toast";
import { formatUSD, formatDate } from "../lib/format";
import type { ReconExportResponse, ReconExportRow } from "@shared/types";

function Diff({ from, to }: { from: string | null; to: string | null }) {
  const a = from ?? "—";
  const b = to ?? "—";
  if (a === b) return <span className="text-ink-700">{b}</span>;
  return (
    <span className="text-ink-700">
      <span className="text-ink-400 line-through">{a}</span>
      <span className="mx-1 text-ink-300">→</span>
      <span className="font-medium text-ink-900">{b}</span>
    </span>
  );
}

export default function Activity() {
  const { activeClientId } = useAuth();
  const toast = useToast();
  const { data, loading, error, refetch } = useApi<ReconExportResponse>(
    `/api/activity${qs({ clientId: activeClientId })}`
  );
  const [revertingId, setRevertingId] = useState<number | null>(null);

  async function revert(row: ReconExportRow) {
    if (!confirm(`Revert this movement to its originally-ingested values?`)) return;
    setRevertingId(row.id);
    try {
      await api.patch(`/api/reconciliation/${row.id}`, { revertToSource: true });
      toast.success("Movement reverted to original");
      refetch();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not revert movement");
    } finally {
      setRevertingId(null);
    }
  }

  if (loading) {
    return (
      <>
        <PageHeader title="Activity" subtitle="Edits made in-app since the last Excel refresh." />
        <KpiSkeletonRow count={1} />
      </>
    );
  }
  if (error) {
    return (
      <>
        <PageHeader title="Activity" />
        <ErrorState message={error} onRetry={refetch} />
      </>
    );
  }

  const rows: ReconExportRow[] = data?.rows ?? [];

  return (
    <>
      <PageHeader
        title="Activity"
        subtitle="Reassignments and reconciliations made in-app since the last Excel refresh. Shows the latest value per movement (not a full edit history); use Revert to restore the original."
      />
      {rows.length === 0 ? (
        <EmptyState
          title="No changes yet"
          message="No movements have been reassigned or reconciled since the last data ingest."
          icon="✎"
        />
      ) : (
        <div className="max-h-[40rem] overflow-auto rounded-lg border border-ink-200">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-500">
              <tr>
                <th className="px-3 py-2 font-medium">Movement</th>
                <th className="px-3 py-2 text-right font-medium">Amount</th>
                <th className="px-3 py-2 font-medium">Project</th>
                <th className="px-3 py-2 font-medium">Category</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Edited by</th>
                <th className="px-3 py-2 font-medium">When</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100 bg-white">
              {rows.map((r) => (
                <tr key={r.rowRef} className="align-top">
                  <td className="px-3 py-2">
                    <div className="font-medium text-ink-900">{r.counterparty ?? "—"}</div>
                    <div className="text-xs text-ink-400">{formatDate(r.date)} · {r.movementType}</div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-ink-900">{formatUSD(r.amount)}</td>
                  <td className="px-3 py-2"><Diff from={r.original.project} to={r.current.project} /></td>
                  <td className="px-3 py-2"><Diff from={r.original.sovStage} to={r.current.sovStage} /></td>
                  <td className="px-3 py-2"><Diff from={r.original.matchStatus} to={r.current.matchStatus} /></td>
                  <td className="px-3 py-2 text-xs text-ink-500">{r.editedByEmail ?? "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-ink-500">{r.editedAt ? formatDate(r.editedAt) : "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => revert(r)}
                      disabled={revertingId === r.id}
                      className="rounded-md border border-ink-300 px-2.5 py-1 text-xs font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-50"
                      title="Restore this movement's original project, category and status"
                    >
                      {revertingId === r.id ? "Reverting…" : "Revert"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
